import * as React from 'react';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/browser';
import 'lazysizes';

import Spinner from 'components/spinner/Spinner';

import { RouterProvider } from 'libs/router';
import FatalErrorLayout from 'layouts/FatalErrorLayout';
import RuntimeConfigProvider from 'layouts/RuntimeConfigProvider';

import store from 'redux-store/store';

import { redirects, routes } from '../routes';

import { setInstagramConsent, setTikTokConsent, setYoutubeConsent } from 'redux-store/reducers/consentReducer';

import { changeLocation } from 'redux-store/reducers/router-reducer';
import { routeUpdated as routeUpdatedForCommon } from 'redux-store/reducers/common-reducer';
import { trackInitialization, trackPageChanged } from 'modules/tracking/utils/tracking';
import { ConsentService, getConsentAsync, waitForUsercentrics } from 'utils/consents';

import type { LocationDeprecated } from 'types';

type Props = {
  onUpdate: (location: LocationDeprecated, prevPathname: string | null) => void
};

type State = {
  hasFatalError: boolean,
  usercentricLoaded: boolean
};

class App extends React.Component<Props, State> {
  state = {
    hasFatalError: false,
    usercentricLoaded: process.env.NODE_ENV === 'development'
  };

  async componentDidMount() {
    const hasUC = await waitForUsercentrics();
    if (hasUC) {
      trackInitialization();

      this.checkInstagramConsent();
      this.checkYouTubeConsent();
      this.checkTikTokConsent();
    }
    this.setState({
      usercentricLoaded: true
    });
  }

  componentDidCatch(error: Error, errorInfo: {}) {
    // Send unexpected error boundary errors to Sentry
    Sentry.withScope(scope => {
      scope.setExtras(errorInfo);
      Sentry.captureException(error);
    });

    this.setState({
      hasFatalError: true
    });
  }

  private async checkInstagramConsent() {
    const hasIGConsent = await getConsentAsync(ConsentService.INSTAGRAM);
    store.dispatch(setInstagramConsent(hasIGConsent));
  }

  private async checkYouTubeConsent() {
    const hasYTConsent = await getConsentAsync(ConsentService.YOUTUBE);
    store.dispatch(setYoutubeConsent(hasYTConsent));
  }

  private async checkTikTokConsent() {
    const hasTTConsent = await getConsentAsync(ConsentService.TIKTOK);
    store.dispatch(setTikTokConsent(hasTTConsent));
  }

  handleRouteUpdate = (nextLocation: LocationDeprecated, prevLocation?: LocationDeprecated) => {
    const { path } = nextLocation;

    if (path === prevLocation?.path) {
      return;
    }

    store.dispatch(changeLocation(nextLocation)).then(() => {
      store.dispatch(routeUpdatedForCommon());
      trackPageChanged({
        url: path
      });
    });

    this.props.onUpdate(nextLocation, prevLocation?.path ?? null);
  };

  render() {
    const { hasFatalError, usercentricLoaded } = this.state;

    if (!usercentricLoaded) {
      return (
        <Spinner />
      );
    }

    if (hasFatalError) {
      return (
        <FatalErrorLayout />
      );
    }

    return (
      <Provider store={store}>
        <React.StrictMode>
          <RuntimeConfigProvider />
          <RouterProvider
            routes={routes() as any}
            redirects={redirects()}
            store={store}
            onUpdate={this.handleRouteUpdate}
          />
        </React.StrictMode>
      </Provider>
    );
  }
}

export default App;
